import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  ConnectionProvider,
  WalletProvider,
  useWallet,
  useConnection,
} from "@solana/wallet-adapter-react";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import type { WalletName } from "@solana/wallet-adapter-base";
import { clusterApiUrl } from "@solana/web3.js";

// RPC endpoint - use mainnet for production
// `||` (not `??`) so an empty-string build arg also falls through to the default
const SOLANA_RPC_URL =
  import.meta.env.VITE_SOLANA_RPC_URL || clusterApiUrl("mainnet-beta");

// Create wallet adapter instance
const phantomAdapter = new PhantomWalletAdapter();
const wallets = [phantomAdapter];

interface WalletContextValue {
  connected: boolean;
  connecting: boolean;
  publicKey: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  signMessage: (message: string) => Promise<string>;
}

const WalletContext = createContext<WalletContextValue | null>(null);

function WalletContextProvider({ children }: { children: ReactNode }) {
  const { connected, connecting, publicKey, wallet, select, connect, disconnect, signMessage } =
    useWallet();

  // Track if we're waiting to connect after wallet selection
  const pendingConnect = useRef(false);

  // Auto-connect when wallet is selected but not connected
  useEffect(() => {
    if (wallet && !connected && !connecting && pendingConnect.current) {
      pendingConnect.current = false;
      connect().catch(console.error);
    }
  }, [wallet, connected, connecting, connect]);

  const value = useMemo(
    () => ({
      connected,
      connecting,
      publicKey: publicKey?.toBase58() ?? null,
      connect: async () => {
        // Must select a wallet before connecting
        if (!wallet) {
          // Mark that we want to connect after selection
          pendingConnect.current = true;
          // Select Phantom - the useEffect above will connect once wallet state updates
          select(phantomAdapter.name as WalletName);
          return;
        }
        await connect();
      },
      disconnect: async () => {
        await disconnect();
      },
      signMessage: async (message: string) => {
        if (!signMessage) {
          throw new Error("Wallet does not support message signing");
        }
        const messageBytes = new TextEncoder().encode(message);
        const signature = await signMessage(messageBytes);
        // Return signature as base64
        return btoa(String.fromCharCode(...signature));
      },
    }),
    [connected, connecting, publicKey, wallet, select, connect, disconnect, signMessage]
  );

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
}

interface WalletProviderWrapperProps {
  children: ReactNode;
}

export function WalletProviderWrapper({ children }: WalletProviderWrapperProps) {
  return (
    <ConnectionProvider endpoint={SOLANA_RPC_URL}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletContextProvider>{children}</WalletContextProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export function useWalletContext() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWalletContext must be used within WalletProviderWrapper");
  }
  return context;
}

// Re-export hooks for convenience
export { useConnection };
